// ante-react-kit/src/components/AntShiftActionOverlay/index.js
// Overlay 2 bước cho thao tác ca làm việc — UI giống AntPasscodeOverlay (frosted glass).
// mode='checkin' (Vào ca / Đổi ca) — bước 1 PIN lookup nhân viên, bước 2 nhập tiền thực nhận.
// mode='checkout' (Cắt ca) — bước 1 PIN xác nhận, bước 2 nhập tiền thực kiểm.
import React, { Fragment, useState } from 'react';
import ReactDOM from 'react-dom';
import { NumberBox } from 'devextreme-react';
import AntPasscodeOverlay from '../AntPasscodeOverlay';
import AntChangePasscodePopup from '../AntChangePasscodePopup';
import AntAuth from '../../utils/AntAuth';
import AntLocalization from '../../utils/AntLocalization';
import cs from '../../utils/const.js';
import '../AntPasscodeOverlay/index.scss';
import '../AntChangePasscodePopup/index.scss';

const _useHRM = () => {
    var s = (AntAuth.getSessionInfo && AntAuth.getSessionInfo('bus_settings')) || {};
    return Number(s.use_Module_HRM) === 1;
};

// Format tiền theo Sys_BaseCurrency + Sys_BaseLanguage + Sys_BaseCurrency_Show_Symbol.
// Đa quốc gia: VND không decimal, USD/EUR 2 decimal, JPY 0 decimal — AntLocalization tự xử lý.
const fmtMoney = (n, showSign) => {
    var v = Number(n) || 0;
    return AntLocalization.translateCurrency(v, !!showSign);
};

export default function AntShiftActionOverlay(props) {
    const { mode, session, onDone, onClose, forced } = props;
    // forced=true → overlay không thể tắt: ẩn ×, vô hiệu click backdrop & ESC.
    // Lối thoát duy nhất là Vào ca thành công hoặc Đăng xuất.
    const effectiveOnClose = forced ? undefined : onClose;
    const handleForcedLogout = () => {
        if (window.confirm('Bạn chắc chắn muốn đăng xuất?')) {
            AntAuth.logOut();
        }
    };
    const [step, setStep] = useState(1); // 1 = PIN entry, 2 = actual_amt confirm
    const [passCode, setPassCode] = useState('');
    const [prepData, setPrepData] = useState(null);
    const [actualAmt, setActualAmt] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [errorKey, setErrorKey] = useState(0);
    const [showChangePin, setShowChangePin] = useState(false);
    const [changedNotice, setChangedNotice] = useState('');
    // Bump để re-render đọc lại flag từ localStorage sau khi đổi PIN / refresh session.
    const [, setSessionVer] = useState(0);

    const useHRM = _useHRM();

    const reportError = (msg) => {
        setErrorText(msg || 'Lỗi');
        setErrorKey((k) => k + 1);
    };

    // Đổi ca = mode checkin AND đang trong ca (có shift_id hoặc session_status InProgress);
    // còn lại (employee_code leftover từ phiên cũ nhưng đã out) thì là Vào ca.
    const isInShift =
        session &&
        (session.employee_session_status === 'InProgress' || Boolean(session.shift_id));
    // HRM tắt → 1 user 1 ca, không có khái niệm "Đổi ca".
    const titleStep1 = useHRM
        ? (mode === 'checkin' ? (isInShift ? 'Đổi ca' : 'Vào ca') : 'Cắt ca')
        : (mode === 'checkin' ? 'Vào ca' : 'Cắt ca');
    const storeName = (session && session.store_name) || '';
    // Cắt ca: hiện rõ ca hiện tại của ai để user biết mình đang cắt ca của người nào
    // (đặc biệt khi user đang dùng máy không phải chính mình).
    const currentEmpName =
        (session && (session.employee_name || session.employee_code)) || '';
    const subtitleStep1 =
        mode === 'checkout' && currentEmpName
            ? (storeName ? storeName + ' · ' : '') + 'Ca của ' + currentEmpName
            : storeName;

    // Bước 1: PIN
    const handlePinComplete = (pin) => {
        if (processing) return;
        setProcessing(true);
        setPassCode(pin);
        const cb = (res) => {
            setProcessing(false);
            if (!res || res.error) {
                // HRM tắt + user chưa cài PIN cá nhân → hướng dẫn vào màn user account.
                const errCode = res && res.error && res.error.code;
                if (!useHRM && errCode === 'passcode_not_set') {
                    reportError('Bạn chưa cài Mã PIN cá nhân. Vào màn Tài khoản người dùng để cài đặt.');
                    setPassCode('');
                    return;
                }
                const msg =
                    (res && res.error && res.error.message && (res.error.message.vi || res.error.message.en)) ||
                    (res && res.error && res.error.content) ||
                    'Mã PIN không đúng';
                reportError(msg);
                setPassCode('');
                return;
            }
            const data = (res && res.data) || {};
            setPrepData(data);
            // Mặc định tiền thực = số dư hệ thống để user chỉ chỉnh nếu lệch.
            const sysAmt =
                mode === 'checkin'
                    ? Number(data.balance) || 0
                    : Number(data.balance) || 0;
            setActualAmt(sysAmt);
            setStep(2);
        };
        if (mode === 'checkin') {
            AntAuth.prepareCheckinByPin(pin, cb);
        } else {
            AntAuth.prepareCheckoutByPin(pin, cb);
        }
    };

    // Bước 2: confirm
    const handleConfirm = () => {
        if (processing) return;
        setProcessing(true);
        const cb = (res) => {
            setProcessing(false);
            if (!res || res.error) {
                const msg =
                    (res && res.error && res.error.message && (res.error.message.vi || res.error.message.en)) ||
                    (res && res.error && res.error.content) ||
                    'Có lỗi xảy ra';
                reportError(msg);
                return;
            }
            if (onDone) onDone(res && res.data);
        };
        if (mode === 'checkin') {
            AntAuth.verifyCheckinByPin(passCode, Number(actualAmt) || 0, cb);
        } else {
            AntAuth.verifyCheckoutByPin(passCode, Number(actualAmt) || 0, cb);
        }
    };

    const handleBack = () => {
        setStep(1);
        setPassCode('');
        setPrepData(null);
        setErrorText('');
    };

    if (showChangePin) {
        const onChanged = (data) => {
            setShowChangePin(false);
            const tgt = data && data.target;
            const who = tgt === 'employee' ? (data.employee_name || data.employee_code) : (data.user_name || data.user_code);
            setChangedNotice(`Đã đổi PIN ${tgt === 'employee' ? 'nhân viên' : 'tài khoản'}: ${who || ''}`);
            // Optimistic: clear flag default PIN ngay trong localStorage để ghi chú tự ẩn,
            // không phải đợi refreshToken (async fire-and-forget) hoàn tất mới hide.
            try {
                var _k = cs.System_Code + '-session-info';
                var _info = JSON.parse(localStorage.getItem(_k) || '{}');
                if (tgt === 'employee') {
                    _info.has_default_employee_pin = 0;
                    if (!_info.employee_code) _info.is_default_user_pin = 0;
                } else {
                    _info.is_default_user_pin = 0;
                    if (!_info.employee_code) _info.has_default_employee_pin = 0;
                }
                localStorage.setItem(_k, JSON.stringify(_info));
            } catch (e) { /* ignore */ }
            try {
                AntAuth.refreshToken(false, null, () => setSessionVer((v) => v + 1));
            } catch (e) { /* ignore */ }
            setSessionVer((v) => v + 1);
        };
        return <AntChangePasscodePopup onClose={() => setShowChangePin(false)} onChanged={onChanged} />;
    }

    if (step === 1) {
        const bottomButtons = (
            <Fragment>
                <button type="button" className="apt-passcode-secondary-btn" onClick={() => setShowChangePin(true)}>
                    Đổi mã PIN
                </button>
                {forced ? (
                    <button type="button" className="apt-passcode-secondary-btn" onClick={handleForcedLogout}>
                        Đăng xuất
                    </button>
                ) : null}
            </Fragment>
        );
        // Bước 1 nhập PIN nhân viên (any employee in scope) → chỉ hiện ghi chú khi BE
        // báo có ít nhất 1 nhân viên đang dùng PIN mặc định '111111'.
        const showDefaultPinHint = Number(AntAuth.getSessionInfo('has_default_employee_pin')) === 1;
        const helperText = (showDefaultPinHint || changedNotice) ? (
            <span>
                {showDefaultPinHint ? <>Mã mặc định: <strong>111111</strong>. Có thể đổi mã ở nút bên dưới.</> : null}
                {showDefaultPinHint && changedNotice ? <br /> : null}
                {changedNotice ? changedNotice : null}
            </span>
        ) : null;
        return (
            <AntPasscodeOverlay
                title={titleStep1}
                subtitle={subtitleStep1}
                processing={processing}
                errorText={errorText}
                errorKey={errorKey}
                onComplete={handlePinComplete}
                dismissible={!forced}
                onDismiss={effectiveOnClose}
                bottomActions={bottomButtons}
                helperText={helperText}
                pinLabel={useHRM ? 'Nhập mã PIN' : 'Nhập Mã PIN cá nhân'}
            />
        );
    }

    // Step 2 — render qua AntPasscodeOverlay với topSlot + bottomActions, ẩn ô PIN.
    const sysAmt = Number(prepData.balance) || 0;
    const diff = Number(actualAmt) - sysAmt;
    const numberBoxFormat = AntLocalization.getCurrencyFormat();

    // checkin: nhân viên = người vừa nhập PIN (prepData)
    // checkout: nhân viên = chủ ca hiện tại (prepData ưu tiên, fallback session)
    const employeeName =
        (prepData && (prepData.employee_name || prepData.employee_code)) ||
        (mode === 'checkout' ? currentEmpName : '');
    const employeeBlock = employeeName ? (
        <div className="apt-shift-row" style={{ borderBottom: '1px solid #e2e8f0', marginBottom: 4 }}>
            <span className="apt-shift-label">{mode === 'checkout' ? 'Ca của' : 'Nhân viên'}</span>
            <span className="apt-shift-value">{employeeName}</span>
        </div>
    ) : null;

    const switchBanner =
        mode === 'checkin' && prepData.is_switch_shift ? (
            <div
                style={{
                    background: '#fef3c7',
                    border: '1px solid #fde68a',
                    color: '#b45309',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 13,
                    marginBottom: 12
                }}
            >
                Sẽ tự động cắt ca của <b>{prepData.current_employee_name || prepData.current_employee_code}</b>.
            </div>
        ) : null;

    const checkoutSummary = mode === 'checkout' && (
        <Fragment>
            <div className="apt-shift-row">
                <span className="apt-shift-label">Số dư đầu ca</span>
                <span className="apt-shift-value">{fmtMoney(Number(prepData.opening_amt) || 0, false)}</span>
            </div>
            <div className="apt-shift-row">
                <span className="apt-shift-label">Tổng thu trong ca</span>
                <span className="apt-shift-value">{fmtMoney(Number(prepData.total_received_in_shift) || 0, true)}</span>
            </div>
            <div className="apt-shift-row">
                <span className="apt-shift-label">Tổng chi trong ca</span>
                <span className="apt-shift-value">{fmtMoney(-(Number(prepData.total_issued_in_shift) || 0), true)}</span>
            </div>
        </Fragment>
    );

    const diffChip =
        diff !== 0 ? (
            <div className={`apt-shift-diff-chip ${diff < 0 ? 'is-low' : 'is-high'}`}>
                {diff < 0 ? `Thiếu ${fmtMoney(-diff, false)}` : `Thừa ${fmtMoney(diff, false)}`}
            </div>
        ) : null;

    const topSlot = (
        <div className="apt-shift-top">
            {switchBanner}
            {employeeBlock}
            {prepData.cash_account_code ? (
                <div className="apt-shift-row">
                    <span className="apt-shift-label">Két</span>
                    <span className="apt-shift-value">{prepData.cash_account_name || prepData.cash_account_code}</span>
                </div>
            ) : null}
            {checkoutSummary}
            <div className="apt-shift-row">
                <span className="apt-shift-label">Số dư hệ thống</span>
                <span className="apt-shift-value">{fmtMoney(sysAmt, false)}</span>
            </div>
            <div className="apt-shift-row">
                <span className="apt-shift-label">{mode === 'checkin' ? 'Tiền thực nhận' : 'Tiền thực kiểm'}</span>
                <NumberBox
                    value={actualAmt}
                    onValueChanged={(e) => setActualAmt(e.value)}
                    format={numberBoxFormat}
                    width={180}
                    showSpinButtons={false}
                    inputAttr={{ style: 'text-align: right' }}
                />
            </div>
            {diffChip}
        </div>
    );

    const bottomActions = (
        <Fragment>
            <button type="button" className="apt-passcode-secondary-btn" onClick={handleBack}>
                Quay lại
            </button>
            <button
                type="button"
                className="apt-passcode-secondary-btn"
                style={{ background: '#3b82f6', color: '#fff', borderColor: '#3b82f6' }}
                onClick={handleConfirm}
            >
                Xác nhận
            </button>
        </Fragment>
    );

    // Step 2: hiển thị card không có PIN box. Re-use overlay shell qua component thuần.
    const step2Node = (
        <div
            className="apt-passcode-overlay"
            onMouseDown={(e) => e.target === e.currentTarget && effectiveOnClose && effectiveOnClose()}
        >
            <div className="apt-passcode-card">
                {effectiveOnClose && (
                    <button type="button" className="apt-passcode-close" onClick={effectiveOnClose} aria-label="close">
                        ×
                    </button>
                )}
                <div className="apt-passcode-title">
                    {mode === 'checkin'
                        ? prepData.is_switch_shift
                            ? 'Đổi ca — xác nhận'
                            : 'Vào ca — xác nhận'
                        : 'Cắt ca — xác nhận'}
                </div>
                <div className="apt-passcode-subtitle">{subtitleStep1}</div>
                {topSlot}
                {errorText ? <div className="apt-passcode-error">{errorText}</div> : null}
                <div className="apt-passcode-bottom">{bottomActions}</div>
                {processing ? (
                    <div className="apt-passcode-processing">
                        <div className="apt-spinner" />
                    </div>
                ) : null}
            </div>
        </div>
    );

    if (typeof document === 'undefined' || !document.body) return step2Node;
    return ReactDOM.createPortal(step2Node, document.body);
}
